/**
 * A RADIX-2 FFT, FOR MEASUREMENT ONLY.
 *
 * Nothing the game plays goes through this. The sea is built by filtering noise around a ring
 * (filters.ts) rather than in the frequency domain, so the synthesis path never needs a
 * transform at all; this exists so the lane can report a spectral centroid and a band breakdown
 * as MEASURED numbers instead of describing the sound in adjectives, which is the one thing a
 * builder who cannot hear must never do.
 *
 * It is thirty lines and it is here rather than in a package because a dependency has to earn
 * itself against this run's idle-cost bar, and a transform used only by tests earns nothing.
 * Iterative Cooley-Tukey, bit-reversal permutation first, in place, power-of-two lengths only.
 */

/** In-place forward FFT. `re` and `im` must be the same power-of-two length. */
export function fftInPlace(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  if (n <= 1) return;
  if ((n & (n - 1)) !== 0) throw new Error(`fft needs a power-of-two length, got ${n}`);

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = re[i] as number;
      re[i] = re[j] as number;
      re[j] = tr;
      const ti = im[i] as number;
      im[i] = im[j] as number;
      im[j] = ti;
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const ur = re[i + k] as number;
        const ui = im[i + k] as number;
        const xr = re[i + k + len / 2] as number;
        const xi = im[i + k + len / 2] as number;
        const vr = xr * cr - xi * ci;
        const vi = xr * ci + xi * cr;
        re[i + k] = ur + vr;
        im[i + k] = ui + vi;
        re[i + k + len / 2] = ur - vr;
        im[i + k + len / 2] = ui - vi;
        const nr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = nr;
      }
    }
  }
}

/**
 * The magnitude spectrum of one Hann-windowed block, as bins 0..n/2.
 *
 * Hann rather than rectangular because a rectangular window on a noise block leaks across the
 * whole spectrum and would put energy in the top octave that the signal does not have - which
 * is precisely the figure this lane cares most about being honest.
 */
export function magnitudeSpectrum(block: Float32Array): Float64Array {
  const n = block.length;
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n);
    re[i] = (block[i] as number) * w;
  }
  fftInPlace(re, im);
  const half = n / 2;
  const mag = new Float64Array(half + 1);
  for (let k = 0; k <= half; k++) {
    mag[k] = Math.hypot(re[k] as number, im[k] as number);
  }
  return mag;
}
